import { defineStore } from "pinia";
import { StoreModule } from "@/constants/store.ts";
import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";

export interface TutorialStoreState {
  isNewUser: boolean;
  showTrialModeStartedNotice: boolean | undefined;
}

const defaultState: TutorialStoreState = {
  isNewUser: false,
  showTrialModeStartedNotice: false,
};
/**
 * The Tutorial Store module manages the state related to trial mode notices and demo mode popups.
 *
 * This module is responsible for:
 * - Tracking whether a user is new to the system
 * - Managing the visibility of demo mode acknowledgment popups
 * - Controlling trial mode started notices
 * - Providing an API to reset trial notices
 *
 * The module works in conjunction with the workspace module to handle transitions
 * between demo mode and trial mode, ensuring users are properly notified when
 * their workspace status changes.
 *
 * Key features:
 * - Persists user acknowledgment of demo mode popups
 * - Provides a mechanism to reset trial notices via API call
 * - Stores flags that control the display of modal dialogs for trial/demo transitions
 *
 * This module is primarily used by:
 * - App.vue to display trial started modals
 * - DemoCountdownNotice component to manage demo mode acknowledgment
 * - Workspace module when setting workspace data that affects trial status
 */
export const useTutorialStore = defineStore(StoreModule.TUTORIAL, {
  state: (): TutorialStoreState => ({ ...defaultState }),
  actions: {
    async resetTrialNotice() {
      const request = api.resetTrialNotice();
      try {
        await axiosInstance.request(request);
        this.showTrialModeStartedNotice = false;
      } catch (e) {
        console.error(e);
      }
    },
  },
});
